import { useState } from 'react'
import type { CreateJobInput, Job, JobPriority, JobType, PayRateType } from '@workflow/contracts'
import { Button, Dropdown, Field, Icons, Loading, Segmented, TextArea, TextField, cn } from '@workflow/ui'
import { errorSentence } from '@/lib/api/client'
import { useClients, useCreateJob, useCreateJobType, useJobTypes, useSession } from '@/lib/api/hooks'
import { navigate } from '@/app/router'
import { PosterDialog } from '@/shell/PosterDialog'
import { ClientCreateDialog } from '@/features/clients/ClientCreateDialog'
import { PRIORITY_LABEL } from './jobsStatus'

/**
 * The questions the slate asks, in the order it asks them. A job opened from
 * a client's page already knows its answer to the first, so the slate skips
 * it — opening that job costs one answer fewer.
 */
const QUESTIONS = {
  client: {
    question: 'Who is it for?',
    hint: 'The client the work belongs to — or the studio itself.',
  },
  title: {
    question: 'What are we making?',
    hint: 'Name the work and give everyone assigned to it the same instructions.',
  },
  type: {
    question: 'What category is it?',
    hint: 'The category the work falls under.',
  },
  hours: {
    question: 'How many hours is it?',
    hint: 'The size of the work — the pool each player’s hours are assigned out of. Leave it open if you don’t know yet.',
  },
  pay: {
    question: 'What does it pay?',
    hint: "Set the rates now or later. The client's rate is never shown to players; the player's rate is hidden until you turn it on.",
  },
  priority: {
    question: 'How urgent is it?',
    hint: 'You can add all the details on the job page after creating.',
  },
} as const

type QuestionKey = keyof typeof QUESTIONS

const FROM_LEDGER: readonly QuestionKey[] = ['client', 'title', 'type', 'hours', 'pay', 'priority']
const FROM_CLIENT: readonly QuestionKey[] = ['title', 'type', 'hours', 'pay', 'priority']

/**
 * Bringing a job in: one question at a time, floating over the ledger. The
 * poster is the desk the work starts on; the form asks only what a job *is*
 * — whose it is, its name, its kind, its urgency — and the window, the words
 * and the crew wait for the job's own page, where they are edited anyway.
 * Opening a job should cost four answers, not a form.
 */
export function JobCreateDialog({
  open,
  onClose,
  initialClientId,
}: {
  open: boolean
  onClose: () => void
  /** Set when the job is opened from a client's page: the who is answered. */
  initialClientId?: string
}) {
  const steps = initialClientId ? FROM_CLIENT : FROM_LEDGER
  const [step, setStep] = useState(0)
  const [clientId, setClientId] = useState(initialClientId ?? '')
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [typeId, setTypeId] = useState('')
  const [estimatedHours, setEstimatedHours] = useState('')
  const [currency, setCurrency] = useState('BDT')
  const [clientRateType, setClientRateType] = useState<PayRateType | ''>('')
  const [clientRateAmount, setClientRateAmount] = useState('')
  const [playerRateType, setPlayerRateType] = useState<PayRateType | ''>('')
  const [playerRateAmount, setPlayerRateAmount] = useState('')
  const [priority, setPriority] = useState<JobPriority>('medium')
  const [clientAdding, setClientAdding] = useState(false)
  const [typeAdding, setTypeAdding] = useState(false)
  const [newTypeName, setNewTypeName] = useState('')
  const [newTypeDescription, setNewTypeDescription] = useState('')
  const clients = useClients()
  const types = useJobTypes()
  const create = useCreateJob()
  const createType = useCreateJobType()
  const isAdmin = useSession().data?.account?.role === 'admin'

  const close = () => {
    onClose()
    setStep(0)
    setClientId(initialClientId ?? '')
    setTitle('')
    setDescription('')
    setTypeId('')
    setEstimatedHours('')
    setCurrency('BDT')
    setClientRateType('')
    setClientRateAmount('')
    setPlayerRateType('')
    setPlayerRateAmount('')
    setPriority('medium')
    setClientAdding(false)
    setTypeAdding(false)
    setNewTypeName('')
    setNewTypeDescription('')
  }

  // A new job is always a draft: it has no crew yet, so nobody — players or
  // Discord — hears of it until it is published from its own page.
  const submit = () => {
    const input: CreateJobInput = { title: title.trim(), typeId, priority }
    if (description.trim()) input.description = description.trim()
    if (clientId) input.clientId = clientId
    if (estimatedHours.trim() !== '' && Number.isFinite(Number(estimatedHours))) {
      input.estimatedHours = Number(estimatedHours)
    }
    if (currency.trim() !== '') input.currency = currency.trim().toUpperCase()
    if (clientRateType !== '') {
      input.clientRateType = clientRateType
      if (clientRateAmount !== '' && Number.isFinite(Number(clientRateAmount))) {
        input.clientRateAmount = Math.round(Number(clientRateAmount) * 100)
      }
    }
    if (playerRateType !== '') {
      input.playerRateType = playerRateType
      if (playerRateAmount !== '' && Number.isFinite(Number(playerRateAmount))) {
        input.playerRateAmount = Math.round(Number(playerRateAmount) * 100)
      }
    }
    create.mutate(input, {
      onSuccess: (data) => {
        const job = data as Job
        close()
        navigate({ name: 'job', id: job.id })
      },
    })
  }

  const advance = () => {
    if (current === 'type' && typeAdding) {
      createType.mutate(
        { name: newTypeName.trim(), description: newTypeDescription.trim() || null },
        {
          onSuccess: (data) => {
            setTypeId((data as JobType).id)
            setTypeAdding(false)
            setNewTypeName('')
            setNewTypeDescription('')
            void types.refetch()
          },
        },
      )
      return
    }
    if (step < steps.length - 1) setStep(step + 1)
    else submit()
  }

  const current = steps[step]!
  const hoursInvalid = estimatedHours.trim() !== '' && !Number.isFinite(Number(estimatedHours))
  const canAdvance =
    current === 'title' ? title.trim() !== '' : current === 'type' ? (typeAdding ? newTypeName.trim() !== '' && !createType.isPending : typeId !== '') : current === 'hours' ? !hoursInvalid : true
  const clientName = initialClientId ? clients.data?.find((client) => client.id === initialClientId)?.name : null

  return (
    <PosterDialog
      open={open}
      onClose={close}
      labelledBy="job-create-title"
      image="/art/onboarding-shoreline.png"
      colorArtwork
      alt="A blue shoreline painting with orange flowers"
      headline="New Job"
      caps="Workflow · New Job"
    >
      <form
        className="flex min-h-[26rem] flex-col px-6 py-6 sm:px-8 sm:py-7 lg:px-10"
        onSubmit={(event) => {
          event.preventDefault()
          if (canAdvance) advance()
        }}
      >
        <div className="flex items-center justify-between gap-4">
          <p className="text-micro text-muted-foreground uppercase">
            {clientName ? `New Job · ${clientName}` : 'New Job'} · Step {step + 1} of {steps.length}
          </p>
          <div className="flex gap-1.5" aria-hidden>
            {steps.map((_, index) => (
              <span
                key={index}
                className={cn('h-0.5 w-5 rounded-full transition-colors duration-fast', index <= step ? 'bg-foreground' : 'bg-tint')}
              />
            ))}
          </div>
        </div>
        <h3 id="job-create-title" className="mt-3 font-display text-2xl text-foreground">
          {QUESTIONS[current].question}
        </h3>
        <p className="mt-2 text-caption text-muted-foreground">{QUESTIONS[current].hint}</p>

        <div className="mt-6 flex flex-1 flex-col justify-start">
          {current === 'client' ? (
            <div className="flex flex-col gap-2">
              {clients.isLoading ? (
                <Loading label="Loading clients" />
              ) : (
                <Field label="Client" htmlFor="job-create-client" hint="Leave empty for internal work.">
                  <Dropdown
                    id="job-create-client"
                    ariaLabel="Client"
                    value={clientId}
                    options={[
                      { value: '', label: 'No Client (Internal)' },
                      ...(clients.data ?? []).map((client) => ({ value: client.id, label: client.name })),
                    ]}
                    onChange={setClientId}
                  />
                </Field>
              )}
              <div>
                <Button type="button" size="xs" variant="ghost" icon={<Icons.plus />} onClick={() => setClientAdding(true)}>
                  New client
                </Button>
              </div>
            </div>
          ) : current === 'title' ? (
            <div className="flex flex-col gap-4">
              <Field label="Title" htmlFor="job-create-title-field">
                <TextField
                  id="job-create-title-field"
                  value={title}
                  autoFocus
                  placeholder="What the studio calls this work"
                  onChange={(event) => setTitle(event.target.value)}
                />
              </Field>
              <Field label="Work description" htmlFor="job-create-description"
                hint="Optional. Shown to every assigned player; use it for shared instructions.">
                <TextArea id="job-create-description" value={description} rows={3} maxLength={8000}
                  placeholder="What should everyone on this job know?"
                  onChange={(event) => setDescription(event.target.value)} />
              </Field>
            </div>
          ) : current === 'type' ? (
            types.isLoading ? (
              <Loading label="Loading categories" />
            ) : (
              <div className="flex flex-col gap-3">
                {isAdmin ? (
                  <Button type="button" size="xs" variant="ghost" icon={<Icons.plus />}
                    onClick={() => setTypeAdding((adding) => !adding)}>
                    {typeAdding ? 'Choose an existing category' : 'Add a new category'}
                  </Button>
                ) : null}
                {typeAdding ? (
                  <>
                    <Field label="Name" htmlFor="job-create-type-name">
                      <TextField id="job-create-type-name" value={newTypeName} autoFocus
                        placeholder="e.g. Accessibility review" maxLength={80}
                        onChange={(event) => setNewTypeName(event.target.value)} />
                    </Field>
                    <Field label="Description" htmlFor="job-create-type-description" hint="Optional — one line about this category.">
                      <TextField id="job-create-type-description" value={newTypeDescription} maxLength={500}
                        placeholder="What this work involves"
                        onChange={(event) => setNewTypeDescription(event.target.value)} />
                    </Field>
                    {createType.error ? <p className="text-caption text-danger">{errorSentence(createType.error)}</p> : null}
                  </>
                ) : (
                  <Field label="Category" htmlFor="job-create-type">
                    <Dropdown
                      id="job-create-type"
                      ariaLabel="Category"
                      placeholder="Choose a category"
                      value={typeId}
                      options={(types.data ?? []).map((type) => ({
                        value: type.id,
                        label: type.name,
                        description: type.description ?? undefined,
                      }))}
                      onChange={setTypeId}
                    />
                  </Field>
                )}
              </div>
            )
          ) : current === 'hours' ? (
            <Field label="Hours" htmlFor="job-create-hours" error={hoursInvalid ? 'Numbers only — e.g. 40.' : undefined}>
              <TextField
                id="job-create-hours"
                inputMode="decimal"
                autoFocus
                placeholder="e.g. 40"
                value={estimatedHours}
                onChange={(event) => setEstimatedHours(event.target.value)}
              />
            </Field>
          ) : current === 'pay' ? (
            <div className="flex flex-col gap-4">
              <Field label="Currency" htmlFor="job-create-currency">
                <Dropdown
                  id="job-create-currency"
                  ariaLabel="Currency"
                  value={currency}
                  options={[
                    { value: 'BDT', label: 'BDT' },
                    { value: 'USD', label: 'USD' },
                    { value: 'EUR', label: 'EUR' },
                    { value: 'GBP', label: 'GBP' },
                    { value: 'INR', label: 'INR' },
                  ]}
                  onChange={setCurrency}
                />
              </Field>
              <Field label="The client pays" htmlFor="job-create-client-rate" hint="Never shown to players.">
                <div className="flex items-center gap-2">
                  <Dropdown
                    ariaLabel="Client rate basis"
                    className="w-36"
                    value={clientRateType}
                    options={[
                      { value: '', label: 'Not set' },
                      { value: 'hourly', label: 'Per hour' },
                      { value: 'one_time', label: 'One-time' },
                    ]}
                    onChange={(value) => setClientRateType(value as PayRateType | '')}
                  />
                  <TextField
                    id="job-create-client-rate"
                    inputMode="decimal"
                    placeholder={clientRateType === 'one_time' ? 'Whole piece' : 'Per hour'}
                    value={clientRateAmount}
                    onChange={(event) =>
                      setClientRateAmount(event.target.value === '' || Number.isFinite(Number(event.target.value)) ? event.target.value : clientRateAmount)
                    }
                  />
                </div>
              </Field>
              <Field label="A player earns" htmlFor="job-create-player-rate" hint="Hidden from players until you show it.">
                <div className="flex items-center gap-2">
                  <Dropdown
                    ariaLabel="Player rate basis"
                    className="w-36"
                    value={playerRateType}
                    options={[
                      { value: '', label: 'Not set' },
                      { value: 'hourly', label: 'Per hour' },
                      { value: 'one_time', label: 'One-time' },
                    ]}
                    onChange={(value) => setPlayerRateType(value as PayRateType | '')}
                  />
                  <TextField
                    id="job-create-player-rate"
                    inputMode="decimal"
                    placeholder={playerRateType === 'one_time' ? 'Whole piece' : 'Per hour'}
                    value={playerRateAmount}
                    onChange={(event) =>
                      setPlayerRateAmount(event.target.value === '' || Number.isFinite(Number(event.target.value)) ? event.target.value : playerRateAmount)
                    }
                  />
                </div>
              </Field>
            </div>
          ) : (
            <Field label="Priority" htmlFor="job-create-priority">
              <Segmented<JobPriority>
                label="Priority"
                value={priority}
                onChange={setPriority}
                options={(['low', 'medium', 'high', 'critical'] as const).map((value) => ({
                  value,
                  label: PRIORITY_LABEL[value],
                }))}
              />
            </Field>
          )}
        </div>

        <div className="mt-6 flex items-center gap-3">
          {step > 0 ? (
            <Button type="button" variant="ghost" onClick={() => setStep(step - 1)}>
              Back
            </Button>
          ) : (
            <Button type="button" variant="ghost" onClick={close}>
              Cancel
            </Button>
          )}
          <span className="flex-1" />
          <Button type="submit" variant="primary" busy={create.isPending || createType.isPending} disabled={!canAdvance}>
            {typeAdding ? 'Add Category' : step === steps.length - 1 ? 'Draft' : 'Continue'}
          </Button>
        </div>
        {step === steps.length - 1 ? <p className="mt-3 text-caption text-muted-foreground">The job is saved as a draft. Assign players on its page, then publish it — players and Discord hear about it only then.</p> : null}
        {create.error ? <p className="mt-3 text-caption text-danger">{errorSentence(create.error)}</p> : null}
      </form>

      <ClientCreateDialog
        open={clientAdding}
        onClose={() => setClientAdding(false)}
        onCreated={(client) => {
          setClientAdding(false)
          setClientId(client.id)
        }}
      />
    </PosterDialog>
  )
}
